/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable consistent-return */

import { Chart } from 'chart.js/auto';
import { useCallback, useEffect, useRef } from 'react';
import type ChartColors from '../../../../../config/ChartColors';
import useChartJsIntersectionObserver from '../../../../../hooks/intersectionObserver/charts/useChartJsIntersectionObserver';

const useDoughnutTotal = (canvas: any, label: string, amount: number, total: number, color: ChartColors) => {
    const chartRef = useRef<any>(undefined);

    const render = useCallback(() => {
        if (!canvas.current) return;
        if (chartRef.current) return;

        chartRef.current = new Chart(canvas.current, {
            type: 'doughnut',
            data: {
                labels: ['', label],
                datasets: [
                    {
                        data: [total - amount, amount],
                        backgroundColor: color,
                    },
                ],
            },
            options: {
                cutout: '88%', // The space inside the doughnut
                plugins: {
                    tooltip: {
                        enabled: false,
                    },
                    legend: {
                        display: false,
                    },
                },
                elements: {
                    arc: {
                        borderWidth: [5, 0], // Border around the arc, (see bottom doughnut charts on desktop design).
                        borderRadius: 10, // Rounds the tip of the arc.
                        spacing: -2, // Overlaps the arc ends with each other.
                    },
                },
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas, amount]);

    const { hasRenderedRef } = useChartJsIntersectionObserver({
        chartRef: canvas,
        animationDuration: 1000,
        threshold: 0.5,
        render,
    });

    /** render will change if the data changes */
    useEffect(() => {
        if (hasRenderedRef.current) {
            chartRef.current?.destroy();
            render();
        }
    }, [render, hasRenderedRef]);

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            chartRef.current?.destroy();
            chartRef.current = null;
        };
    }, [label, amount, total, color, canvas]);
};

export default useDoughnutTotal;
